import React, { useEffect, useState } from "react";
import { FaSyncAlt, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "../../Styles/Employee1.css";
import ApiService from "../../nhrc_apis/ApiService";

const { MembershipAPI, AuthAPI } = ApiService;

function Employee1({ employeeData, personalData, onBack, onComplete, memberId, membershipId }) {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [captchaKey, setCaptchaKey] = useState("");
  const [inputCaptcha, setInputCaptcha] = useState("");
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (personalData?.email) {
      setEmail(personalData.email);
    }
    fetchCaptcha();
  }, [personalData]);

  const fetchCaptcha = async () => {
    try {
      setCaptchaLoading(true);
      const res = await AuthAPI.getCaptcha();
      console.log("CAPTCHA RESPONSE:", res.data);

      const key = res?.data?.captcha_key || res?.data?.captcha_id || res?.data?.id || "";
      setCaptchaQuestion(res?.data?.question || "");
      setCaptchaKey(String(key));
      setInputCaptcha("");
      setCaptchaVerified(false);
    } catch (err) {
      console.error("CAPTCHA ERROR:", err.response?.data || err);
      alert(err?.response?.data?.detail || "Failed to load captcha");
    } finally {
      setCaptchaLoading(false);
    }
  };

  const handleVerifyCaptcha = async () => {
    if (!inputCaptcha.trim()) {
      setErrors((prev) => ({ ...prev, captcha: "Please enter captcha answer" }));
      return;
    }

    if (!captchaKey) {
      alert("Captcha not loaded properly");
      return;
    }

    try {
      const payload = { captcha_id: String(captchaKey), answer: Number(inputCaptcha) };
      console.log("VERIFY CAPTCHA PAYLOAD:", payload);

      await AuthAPI.verifyCaptcha(payload);

      setCaptchaVerified(true);
      setErrors((prev) => ({ ...prev, captcha: "" }));
      alert("Captcha verified successfully");
    } catch (err) {
      console.error("CAPTCHA VERIFY ERROR:", err.response?.data || err);
      setCaptchaVerified(false);
      setErrors((prev) => ({ ...prev, captcha: "Invalid captcha" }));
      alert(err?.response?.data?.detail || "Invalid captcha");
    }
  };

  const validate = () => {
    let newErrors = {};

    if (!password.trim()) newErrors.password = "Enter password";
    if (!confirmPassword.trim()) newErrors.confirmPassword = "Enter confirm password";

    if (password && password.length < 6) newErrors.password = "Password must be at least 6 characters";
    if (password && !/[A-Z]/.test(password)) newErrors.password = "Password must contain at least one uppercase letter";
    if (password && !/[0-9]/.test(password)) newErrors.password = "Password must contain at least one number";
    if (password && !/[!@#$%^&*]/.test(password)) newErrors.password = "Password must contain at least one special character";

    if (password && confirmPassword && password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!captchaVerified) newErrors.captcha = "Please verify captcha";

    if (!employeeData?.email_otp || employeeData.email_otp.length !== 6) {
      newErrors.otp = "OTP missing or invalid. Please go back.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const isValid = validate();
    if (!isValid) return;

    /* =====================================================
       Declared OUTSIDE the try block (with `let`, no value
       yet) so it's still visible inside the catch block below.
       A `const`/`let` declared INSIDE `try { ... }` only exists
       inside that block — referencing it in `catch` throws
       "'payload' is not defined" (a ReferenceError at runtime,
       and exactly the no-undef ESLint error you hit).
    ===================================================== */
    let payload;

    try {
      setLoading(true);

      payload = {
        member_id: Number(memberId),
        membership_id: String(membershipId),

        organization_name: employeeData?.organization_name || "",
        industry: employeeData?.industry || "",
        designation: employeeData?.designation || "",
        department: employeeData?.department || "",
        company_website: employeeData?.company_website || "",
        working_location: employeeData?.working_location || "",
        company_strength: employeeData?.company_strength || "",
        employee_id: String(employeeData?.employee_id || ""),
        experience: Number(employeeData?.experience || 0),

        official_email: employeeData?.official_email || "",
        user_email: employeeData?.official_email || "",

        email_otp: String(employeeData?.email_otp || ""),
        referral_id: employeeData?.referral_id || "",
        id_card_front: employeeData?.id_card_front || "",
        id_card_back: employeeData?.id_card_back || "",

        password: password.trim(),
        confirm_password: confirmPassword.trim(),

        captcha_id: String(captchaKey),
        captcha_answer: Number(inputCaptcha),
      };

      // Full payload logged BEFORE the call — if this request ever fails
      // with a Network Error again, copy this exact object and the request
      // URL below and test it directly against the backend (Postman/curl)
      // to see the real server-side error, since the browser hides it.
      console.log("🔥 FINAL EMPLOYEE PAYLOAD:", payload);
      console.log("🔥 SUBMITTING TO: /membership/employee-details");

      const res = await MembershipAPI.createEmployee(payload);
      console.log("✅ EMPLOYEE RESPONSE:", res.data);

      const empId = res?.data?.employee_id || res?.data?.id || res?.data?.member_id || "";
      setEmployeeId(empId);
      setShowPopup(true);

      if (onComplete) onComplete(res.data);
    } catch (err) {
      console.error("❌ EMPLOYEE SUBMIT ERROR:", err.response?.data || err);

      /* =====================================================
         NO RESPONSE AT ALL — this is NOT a validation error
         and it is NOT a bad-token error (a bad/expired token
         would come back as a 401 with err.response set). This
         means the request never got a usable response back —
         almost always a backend crash / CORS issue on this
         exact endpoint. Tell the admin clearly instead of a
         generic "Registration Failed" message.
      ===================================================== */
      if (!err.response) {

        alert(
          "The server did not respond while saving employee details " +
          "(this is a backend/network issue, not a login problem). " +
          "Please try again in a moment. If it keeps happening, share " +
          "this payload with the backend team:\n\n" +
          JSON.stringify(payload, null, 2)
        );

        fetchCaptcha();
        return;
      }

      let errorMsg = "Employee Registration Failed";
      if (Array.isArray(err?.response?.data?.detail)) {
        errorMsg = err.response.data.detail[0]?.msg;
      } else if (err?.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }

      alert(errorMsg);
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate("/");
  };

  return (
    <div className="emp1-container">
      {!showPopup ? (
        <form className="emp1-form" onSubmit={handleSubmit}>
          <h3>CREATE ACCOUNT</h3>

          <div style={{ marginBottom: "15px" }}>
            <input className="form-control emp-form-input" value={email} readOnly />
          </div>

          <div style={{ position: "relative", marginBottom: "15px" }}>
            <input
              className="emp-form-input"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors((prev) => ({ ...prev, password: "" }));
              }}
              required
            />
            <span
              onClick={() => setShowPassword(!showPassword)}
              style={{ position: "absolute", right: "10px", top: "12px", cursor: "pointer" }}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
            {errors.password && <small className="text-danger">{errors.password}</small>}
          </div>

          <div style={{ position: "relative", marginBottom: "15px" }}>
            <input
              className="emp-form-input"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrors((prev) => ({ ...prev, confirmPassword: "" }));
              }}
              required
            />
            <span
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{ position: "absolute", right: "10px", top: "12px", cursor: "pointer" }}
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
            {errors.confirmPassword && <small className="text-danger">{errors.confirmPassword}</small>}
          </div>

          <div className="emp1-captcha-box">
            <FaSyncAlt onClick={fetchCaptcha} style={{ cursor: "pointer" }} />
            <span>{captchaLoading ? "Loading..." : captchaQuestion}</span>
            <input
              value={inputCaptcha}
              onChange={(e) => {
                setInputCaptcha(e.target.value);
                setErrors((prev) => ({ ...prev, captcha: "" }));
              }}
              placeholder="Enter captcha"
            />
            <button type="button" onClick={handleVerifyCaptcha} className="verify-btn">
              {captchaVerified ? "Verified" : "Verify"}
            </button>
          </div>

          {errors.captcha && <small className="text-danger">{errors.captcha}</small>}
          {errors.otp && <small className="text-danger">{errors.otp}</small>}

          <div className="emp1-buttons">
            <button type="button" onClick={onBack} className="emp1-btn-secondary">
              Back
            </button>
            <button type="submit" disabled={loading} className="emp1-btn-primary">
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      ) : (
        <div className="emp1-popup-card">
          <h3>Welcome to NHRC 🎉</h3>
          <p><b>Employee ID:</b> {employeeId}</p>
          <p><b>Email:</b> {email}</p>
          <p>
            <b>Password:</b> {showPassword ? password : "********"}
            <span
              style={{ marginLeft: "8px", cursor: "pointer" }}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </p>
          <button className="emp1-continue-btn" onClick={handleContinue}>
            Continue
          </button>
        </div>
      )}
    </div>
  );
}

export default Employee1;